<template>
  <pf-form
    ref="formRef"
    :component="fake ? 'div' : 'form'"
    :method="method"
    :action="action"
    novalidate
    :horizontal="horizontal === true"
    :class="{
      [`pf-m-horizontal-${horizontal}`]: typeof horizontal === 'string',
    }"
    @submit="submit"
  >
    <fieldset ref="fieldset" :class="styles.form" :disabled="disabled || sending ? true : undefined">
      <slot />
    </fieldset>

    <div v-if="!fake" :class="[styles.formGroup, styles.modifiers.action]">
      <pf-alert-group>
        <pf-alert
          v-for="(error, i) in submitErrors"
          :key="i"
          :variant="(error.type as AlertType)"
          inline
          title="Errore di salvataggio"
          @close="submitErrors.splice(i, 1)"
        >
          {{ error.message }}
        </pf-alert>
      </pf-alert-group>

      <div v-if="submitText || $slots.submit || $slots.actions" :class="styles.formGroupControl">
        <div :class="styles.formActions">
          <pf-button
            v-if="submitText || $slots.submit"
            type="submit"
            variant="primary"
            :aria-disabled="!valid"
            :disabled="toValue(effectiveDisabled) || preventSubmit"
            :loading="sending"
            :large="submitBlock"
            :block="submitBlock"
            @click="validate"
          >
            <slot name="submit">{{ submitText }}</slot>
          </pf-button>
          {{ ' ' }}
          <slot name="actions" />
          {{ ' ' }}
          <em v-if="submitHelp">{{ submitHelp }}</em>
        </div>
      </div>
    </div>
  </pf-form>
</template>

<style lang="scss" scoped>
fieldset {
  border: none;
}

.pf-v6-c-form__group.pf-m-action {
  bottom: calc(var(--pf-v6-c-drawer--child--PaddingBottom) * -1);
  position: sticky;
  overflow: initial;
  background-color: var(--pf-v6-c-drawer__panel--BackgroundColor);
  padding-bottom: var(--pf-v6-c-drawer--child--PaddingBottom);
  padding-left: var(--pf-v6-c-drawer--child--PaddingLeft);
  margin-left: calc(var(--pf-v6-c-drawer--child--PaddingLeft) * -1);
  padding-right: var(--pf-v6-c-drawer--child--PaddingRight);
  margin-right: calc(var(--pf-v6-c-drawer--child--PaddingRight) * -1);

  > :deep(.pf-v6-c-alert-group) {
    grid-column: 1 / 3;
  }
}

:deep(.pf-v6-c-alert.pf-m-inline:not(:last-child)) {
  margin-bottom: 20px;
}

/*
// intranet/node_modules/.pnpm/@patternfly+patternfly@5.2.1/node_modules/@patternfly/patternfly/components/Form/form.scss
*/

:deep(.pf-v6-c-form__group) {
  grid-template-columns: var(--pf-v6-c-form--m-horizontal__group-label--md--GridColumnWidth) var(--pf-v6-c-form--m-horizontal__group-control--md--GridColumnWidth);
  grid-column-gap: var(--pf-v6-c-form--m-horizontal__group-label--md--GridColumnGap);
}

:deep(.pf-v6-c-form__group-control) {
  grid-column: 2;
}

@mixin form-horizontal-responsive($suffix, $width) {
  @container form (min-width: #{$width}) {
    .pf-v6-c-form.pf-m-horizontal-#{$suffix} {
      --pf-v6-c-form__group-label--PaddingBottom: 0;

      :deep(.pf-v6-c-form__group) {
        display: grid;
      }

      :deep(.pf-v6-c-form__group-label) {
        padding-block-start: var(--pf-v6-c-form--m-horizontal__group-label--md--PaddingTop);

        &.pf-m-no-padding-top {
          --pf-v6-c-form--m-horizontal__group-label--md--PaddingTop: var(--pf-v6-c-form--m-horizontal__group-label--m-no-padding--md--PaddingTop);
          transform: translateY(var(--pf-v6-c-form--m-horizontal__group-label--m-no-padding--md--TranslateY));
        }
      }

      &.pf-m-align-right {
        :deep(.pf-v6-c-form__label) {
          text-align: end;
        }
      }
    }
  }
}

@include form-horizontal-responsive(sm, 450px);
@include form-horizontal-responsive(md, 650px);
@include form-horizontal-responsive(lg, 900px);
@include form-horizontal-responsive(xl, 1200px);
</style>

<script lang="ts" setup>
import { toValue } from 'vue';
import { useXForm } from '@common/form';
import type { AlertType } from '@/store/alerts';
import styles from '@patternfly/react-styles/css/components/Form/form';
import type { Method } from 'axios';
import { $t as _t } from '@/i18n';
import type { Props as PfFormProps } from '@vue-patternfly/core/components/Form/Form.vue';

export interface Props extends /* @vue-ignore */ Omit<PfFormProps, 'horizontal' | 'novalidate' | 'component' | 'onSubmit'> {
  action?: string;
  submitText?: string;
  submitHelp?: string;
  submitBlock?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  encoding?: 'form' | 'json';
  fake?: boolean;
  horizontal?: boolean | 'sm' | 'md' | 'lg' | 'xl';
  method?: Method;
  xhr?: boolean;
  preventSubmit?: boolean;
  sending?: boolean;
  additionalData?: Record<string, any> | (() => Record<string, any>);
  onBeforeSubmit?: () => boolean | undefined | void;
  onSubmit?: (e: Event | undefined, responseData?: any) => MaybePromise<void>;
  onCancel?: () => void;
  onCheckValidity?: (valid: boolean) => void;
}

const props = withDefaults(defineProps<Props>(), {
  submitText: _t('Procedi'),
  encoding: 'form',
  horizontal: 'md',
  method: 'post',
  sending: undefined,
  additionalData: () => ({}),
});

defineSlots<{
  default: (props?: Record<never, never>) => any;
  submit: (props?: Record<never, never>) => any;
  actions: (props?: Record<never, never>) => any;
}>();

defineEmits<{
  (name: 'update:sending', value: boolean): void;
}>();

const {
  form,
  formRef,
  fieldset,
  submit,
  submitErrors,
  sending,
  valid,
  validate,
  checkValidity,
  effectiveDisabled,
} = useXForm({
  method: () => props.method,
  disabled: () => props.disabled,
  xhr: () => props.xhr,
  preventSubmit: () => props.preventSubmit,
  action: () => props.action,
  encoding: () => props.encoding,
  sending: () => props.sending,
  additionalData: props.additionalData,
  onBeforeSubmit: props.onBeforeSubmit,
  onSubmit: props.onSubmit,
  onCheckValidity: props.onCheckValidity,
});

defineExpose({
  form,
  fieldset,
  checkValidity,
});
</script>
